import { message } from "antd"
import dayjs from "dayjs"
import { useAtomValue } from "jotai"
import { isEmpty, sortBy } from "lodash"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { portfolioAtoms, studentAtoms } from "#atoms"
import Firebase from "#classes/Firebase"
import { useUserContext } from "#contexts/UserContextProvider"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import type { StudentPortfolioConstructor } from "#customTypes/composedTypes/portfolioTypes"
import {
  PORTFOLIO_AUTHOR_KIND,
  type PortfolioAuthorKind,
} from "#customTypes/composedTypes/portfolioTypes"
import type { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import type { Uuid } from "#customTypes/globalTypes"
import { useOneDoc } from "#hooks/jotai/useSubscription"
import { useArtworkTags } from "#hooks/newHooks/useArtworkTags"
import { resolveArtworkTagTexts } from "#utils/artworkTagHelpers"
import { normalizeArtworkTagIds } from "#utils/artworkTagHelpers"
import { cleanFormData } from "#utils/formHelpers"
import { notifyPortfolioUpdated } from "#utils/portfolioNotifications"
import { type ErrorType, formatError } from "#utils/textFormatters"

import { getUnassignedProjectId } from "./ensurePortfolioExists"
import {
  addToPriorityLane,
  type ArrangementError,
  clampPriorityLane,
  insertNewProjectInArrangement,
  moveProjectToCategoryInArrangement,
  normalizeArrangement,
  PORTFOLIO_PRIORITY_LANE_MAX,
  type ProjectPlacement,
  removeFromPriorityLane,
  reorderPriorityLane,
  reorderProjectInArrangement,
} from "./portfolioArrangement"
import {
  normalizeProjectsForUi,
  studentPortfolioDocToViewModel,
  viewModelToStudentPortfolioDoc,
} from "./portfolioConversions"
import {
  ESSAY_LABELS,
  ESSAY_SELECTION_IDS,
  getEssayKeyFromSelectionId,
} from "./portfolioEssayConstants"

import type {
  CreatePortfolioProjectInput,
  CreatePortfolioStepInput,
  PortfolioMockProfile,
  PortfolioMockProject,
  PortfolioMockReference,
  PortfolioMockStage,
  PortfolioPlanningFilters,
  PortfolioPlanningProjectSummary,
  PortfolioPlanningSortKey,
  PortfolioStatementKey,
  PortfolioTimelineItem,
} from "./portfolioUiTypes"

export type {
  CreatePortfolioProjectInput,
  CreatePortfolioStepInput,
  PortfolioMockProfile,
  PortfolioMockProject,
  PortfolioMockReference,
  PortfolioMockStage,
  PortfolioMockStatement,
  PortfolioPlanningFilters,
  PortfolioPlanningProjectSummary,
  PortfolioPlanningSortKey,
  PortfolioStatementKey,
  PortfolioTimelineItem,
} from "./portfolioUiTypes"

export const createId = (prefix: string): string =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

const ONE_MINUTE_MS = 60 * 1000
const FIVE_MINUTES_MS = 5 * 60 * 1000
export const PORTFOLIO_MAX_STEPS_PER_PROJECT = 50

export { clampPriorityLane, PORTFOLIO_PRIORITY_LANE_MAX }

/**
 * Outcome of a mutation that can be refused by the arrangement rules (category
 * limits, the fixed Unassigned project). Persistence itself is fire-and-forget
 * as before; `ok: false` means nothing was written.
 */
export type PortfolioMutationResult =
  | { ok: true }
  | { ok: false; error: ArrangementError }

const MUTATION_OK: PortfolioMutationResult = { ok: true }

export const pickRandomProjectIdFromLane = (
  laneProjectIds: readonly string[],
  random: () => number = Math.random
): string | undefined => {
  if (!laneProjectIds.length) return undefined
  return laneProjectIds[Math.floor(random() * laneProjectIds.length)]
}

const FEEDBACK_TIMELINE_ITEM_KEYS = new Set([
  "teacherComment",
  "rejectionReason",
])

const STATUS_DISPLAY_LABELS: Record<string, string> = {
  not_started: "Not Started",
  in_progress: "In Progress",
  review: "In Review",
  complete: "Complete",
  rejected: "Has Feedback",
}

export const formatEventValue = (fieldKey: string, value: unknown): string => {
  if (value === undefined || value === null) return ""
  if (Array.isArray(value)) return value.map(String).join(", ")
  if (typeof value !== "string") return String(value)

  if (fieldKey === "dueDate") {
    const parsed = dayjs(value)
    return parsed.isValid() ? parsed.format("MMMM D, YYYY") : value
  }

  if (fieldKey === "status" && STATUS_DISPLAY_LABELS[value]) {
    return STATUS_DISPLAY_LABELS[value]
  }

  if (fieldKey === "status" || fieldKey === "category") {
    return value
      .split("_")
      .filter(Boolean)
      .map((word) => `${word[0]?.toUpperCase() ?? ""}${word.slice(1)}`)
      .join(" ")
  }

  return value
}

export const buildFieldEventMessage = ({
  scope,
  fieldKey,
  value,
}: {
  scope: "project" | "item"
  fieldKey: string
  value: unknown
}): string => {
  const labelsByScope: Record<"project" | "item", Record<string, string>> = {
    project: {
      title: "Title",
      notes: "Notes",
      coverImageUrl: "Cover Image",
      category: "Category",
      planningGroup: "Priority Group",
      tagIds: "Tags",
      dimensions: "Dimensions",
      materials: "Materials",
      processes: "Processes",
      ideas: "Ideas",
    },
    item: {
      label: "Step Name",
      dueDate: "Due Date",
      medium: "Medium",
      notes: "Notes",
      teacherComment: "Teacher Comment",
      feedback: "Feedback",
      rejectionReason: "Feedback Reason",
      status: "Status",
    },
  }

  const label = labelsByScope[scope][fieldKey] ?? fieldKey
  const nextValue = formatEventValue(fieldKey, value)
  if (!nextValue.trim()) return `Cleared ${label}.`
  return `Updated ${label} to ${nextValue}.`
}

const upsertFieldEvent = (
  prev: PortfolioTimelineItem[],
  entry: PortfolioTimelineItem
): PortfolioTimelineItem[] => {
  const isFieldEvent = !!entry.eventFieldKey
  const matchingIndex = prev.findIndex((candidate) => {
    if (candidate.type !== "event" || candidate.createdBy !== entry.createdBy)
      return false
    if (candidate.projectId !== entry.projectId) return false

    if (isFieldEvent) {
      return (
        candidate.eventScope === entry.eventScope &&
        candidate.eventEntityId === entry.eventEntityId &&
        candidate.eventFieldKey === entry.eventFieldKey
      )
    }

    return !candidate.eventFieldKey && candidate.message === entry.message
  })

  if (matchingIndex < 0) {
    return [entry, ...prev]
  }

  const matchingEntry = prev[matchingIndex]
  const matchingCreatedAtMs = dayjs(matchingEntry.createdAt).valueOf()
  const entryCreatedAtMs = dayjs(entry.createdAt).valueOf()
  const window = isFieldEvent ? FIVE_MINUTES_MS : ONE_MINUTE_MS
  const isWithinCoalesceWindow =
    Number.isFinite(matchingCreatedAtMs) &&
    entryCreatedAtMs - matchingCreatedAtMs <= window

  if (!isWithinCoalesceWindow) {
    return [entry, ...prev]
  }

  const next = [...prev]
  next.splice(matchingIndex, 1)
  return [{ ...matchingEntry, ...entry }, ...next]
}

const appendTimelineEntries = (
  prev: PortfolioTimelineItem[],
  entries: PortfolioTimelineItem[]
): PortfolioTimelineItem[] => entries.reduce(upsertFieldEvent, prev)

const withStageSortIndices = (
  project: PortfolioMockProject
): PortfolioMockProject => ({
  ...project,
  stages: project.stages.map((stage, index) => ({
    ...stage,
    sortIndex: index,
  })),
})

const getProjectPlanningSummary = (
  project: PortfolioMockProject
): PortfolioPlanningProjectSummary => {
  const validDueDates = project.stages
    .map((stage) => stage.dueDate)
    .filter((dueDate) => !!dueDate && dayjs(dueDate).isValid())
  const soonestStepDueDate = validDueDates.length
    ? sortBy(validDueDates, (dueDate) => dayjs(dueDate).valueOf())[0]
    : undefined
  const feedbackCount = project.stages.filter(
    (stage) => !!stage.feedback?.trim()
  ).length
  const hasFeedback = project.stages.some((stage) => !!stage.feedback?.trim())
  const lastActivityAt = sortBy(
    project.stages
      .map((stage) => stage.lastActivityAt ?? stage.createdAt ?? stage.dueDate)
      .filter((value): value is string => Boolean(value)),
    (value) => dayjs(value).valueOf()
  ).at(-1)

  const statuses = project.stages.map((stage) => stage.status)
  const aggregatedStatus: PortfolioPlanningProjectSummary["aggregatedStatus"] =
    hasFeedback
      ? "feedback_needed"
      : statuses.some((status) => status === "in_progress")
        ? "in_progress"
        : statuses.some((status) => status === "review")
          ? "review"
          : statuses.every((status) => status === "complete")
            ? "complete"
            : "not_started"

  return {
    projectId: project.id,
    category: project.category,
    soonestStepDueDate,
    lastActivityAt,
    hasFeedback,
    feedbackCount,
    aggregatedStatus,
  }
}

export const filterPlanningProjectIds = (
  projects: PortfolioMockProject[],
  summaries: Record<string, PortfolioPlanningProjectSummary>,
  filters: PortfolioPlanningFilters
): string[] =>
  projects
    .filter((project) => {
      const summary = summaries[project.id]
      if (!summary) return false
      if (filters.group !== "all" && summary.category !== filters.group)
        return false
      if (
        filters.aggregatedStatus !== "all" &&
        summary.aggregatedStatus !== filters.aggregatedStatus
      ) {
        return false
      }
      if (filters.hasFeedback === "has_feedback" && !summary.hasFeedback) {
        return false
      }
      if (filters.hasFeedback === "clear" && summary.hasFeedback) return false
      return true
    })
    .map((project) => project.id)

export const sortPlanningProjectIds = (
  projectIds: string[],
  projectsById: Record<string, PortfolioMockProject>,
  summaries: Record<string, PortfolioPlanningProjectSummary>,
  sortKey: PortfolioPlanningSortKey
): string[] =>
  [...projectIds].sort((a, b) => {
    const projectA = projectsById[a]
    const projectB = projectsById[b]
    const summaryA = summaries[a]
    const summaryB = summaries[b]
    if (!projectA || !projectB || !summaryA || !summaryB) return 0

    if (sortKey === "title") return projectA.title.localeCompare(projectB.title)
    if (sortKey === "feedback")
      return summaryB.feedbackCount - summaryA.feedbackCount
    if (sortKey === "last_activity") {
      return (
        dayjs(summaryB.lastActivityAt).valueOf() -
        dayjs(summaryA.lastActivityAt).valueOf()
      )
    }
    return (
      dayjs(summaryA.soonestStepDueDate).valueOf() -
      dayjs(summaryB.soonestStepDueDate).valueOf()
    )
  })

const mergeProfileWithEphemeralFeedback = (
  base: PortfolioMockProfile,
  ephemeral: Record<PortfolioStatementKey, string>
): PortfolioMockProfile => ({
  ...base,
  statements: {
    sustainedInquiryQuestion: {
      ...base.statements.sustainedInquiryQuestion,
      teacherFeedback:
        ephemeral.sustainedInquiryQuestion ??
        base.statements.sustainedInquiryQuestion.teacherFeedback,
    },
    sustainedPracticeEvidence: {
      ...base.statements.sustainedPracticeEvidence,
      teacherFeedback:
        ephemeral.sustainedPracticeEvidence ??
        base.statements.sustainedPracticeEvidence.teacherFeedback,
    },
  },
})

export const usePortfolioDbStorage = ({
  studentId,
}: {
  studentId: Uuid | null | undefined
}) => {
  const { currentUserId, effectiveUserId } = useUserContext()
  const effectiveStudent = useAtomValue(
    studentAtoms.docByIdAtom(effectiveUserId != null ? effectiveUserId : null)
  )
  const authorKind: PortfolioAuthorKind = effectiveStudent
    ? PORTFOLIO_AUTHOR_KIND.STUDENT
    : PORTFOLIO_AUTHOR_KIND.TEACHER

  const portfolioDoc = useOneDoc<StudentPortfolioConstructor>(
    portfolioAtoms,
    DOC_TYPES.PORTFOLIOS,
    studentId ?? null
  )

  const portfolioDocRef = useRef(portfolioDoc)
  portfolioDocRef.current = portfolioDoc

  const derived = useMemo(
    () => studentPortfolioDocToViewModel(portfolioDoc),
    [portfolioDoc]
  )

  const [ephemeralTeacherFeedback, setEphemeralTeacherFeedback] = useState<
    Record<PortfolioStatementKey, string>
  >({
    sustainedInquiryQuestion: "",
    sustainedPracticeEvidence: "",
  })

  useEffect(() => {
    setEphemeralTeacherFeedback({
      sustainedInquiryQuestion: "",
      sustainedPracticeEvidence: "",
    })
  }, [portfolioDoc?.id, portfolioDoc?.lastUpdatedAt])

  const portfolioProfile = useMemo(
    () =>
      mergeProfileWithEphemeralFeedback(
        derived.portfolioProfile,
        ephemeralTeacherFeedback
      ),
    [derived.portfolioProfile, ephemeralTeacherFeedback]
  )

  const projects = useMemo(
    () => normalizeProjectsForUi(derived.projects),
    [derived.projects]
  )
  const timelineEntries = derived.timelineEntries
  const priorityLaneProjectIds = derived.priorityLaneProjectIds

  // Only for labelling tag timeline events — the picker gets its own
  // useArtworkTags(projects) call, and the subscription is deduped.
  const { tagsById } = useArtworkTags()

  // Holds either a real project id or one of the reserved essay selection ids
  // from ESSAY_SELECTION_IDS (essays behave as pseudo-projects in the UI).
  const [activeProjectId, setActiveProjectId] = useState<string | undefined>()

  const selectedEssayKey = useMemo(
    () => getEssayKeyFromSelectionId(activeProjectId),
    [activeProjectId]
  )

  useEffect(() => {
    // Essay selections are always valid — never reset them to a project.
    if (getEssayKeyFromSelectionId(activeProjectId)) return
    if (!projects.length) {
      setActiveProjectId(undefined)
      return
    }
    const hasActiveProject = projects.some(
      (project) => project.id === activeProjectId
    )
    if (!hasActiveProject) {
      setActiveProjectId(projects[0].id)
    }
  }, [activeProjectId, projects])

  const selectedProject = useMemo(
    () =>
      selectedEssayKey
        ? undefined
        : (projects.find((project) => project.id === activeProjectId) ??
          projects[0]),
    [activeProjectId, projects, selectedEssayKey]
  )

  const selectedWorkProjects = useMemo(
    () => projects.filter((project) => project.category === "selected"),
    [projects]
  )

  const sustainedProjects = useMemo(
    () => projects.filter((project) => project.category === "sustained"),
    [projects]
  )

  const pausedProjects = useMemo(
    () => projects.filter((project) => project.category === "paused"),
    [projects]
  )

  const unassignedProjects = useMemo(
    () => projects.filter((project) => project.category === "unassigned"),
    [projects]
  )

  // `removedArtworkIds` flips `inPortfolio: false` on the student's artwork in
  // the same transaction — `ensurePortfolioExists` treats `false` as an
  // explicit opt-out, so a deleted stage's artwork isn't re-imported.
  const persistPortfolio = useCallback(
    async (
      nextProjects: PortfolioMockProject[],
      nextProfile: PortfolioMockProfile,
      nextTimeline: PortfolioTimelineItem[],
      nextLane: string[],
      removedArtworkIds: string[] = []
    ): Promise<boolean> => {
      if (!studentId || !currentUserId) return false
      const existing = portfolioDocRef.current
      const now = new Date().toISOString()
      const nextDoc = viewModelToStudentPortfolioDoc(
        studentId,
        nextProjects,
        nextProfile,
        nextTimeline,
        clampPriorityLane(nextLane),
        existing,
        {
          createdAt: existing?.createdAt ?? now,
          createdBy: (existing?.createdBy ?? currentUserId) as Uuid,
          lastUpdatedAt: now,
          lastUpdatedBy: currentUserId as Uuid,
        }
      )
      const fb = Firebase.getInstance()
      try {
        // Scanner race defense:
        // The scanner-side worker writes new stages into the Unassigned project
        // on the same portfolio doc. Without a transactional read-modify-write,
        // a full-doc save here would clobber any stage the scanner added after
        // our subscription last synced. We do a three-way merge inside a
        // transaction: compare local, last-seen (`existing`), and fresh remote,
        // and preserve any Unassigned stages the scanner added in the window.
        // With no baseline (doc created after our last sync), every remote
        // project we never saw is preserved.
        const unassignedId = getUnassignedProjectId(studentId as Uuid)
        await fb.runTransaction(async (transaction) => {
          const portfolioRef = fb.getRefById<StudentPortfolioConstructor>(
            DOC_TYPES.PORTFOLIOS,
            studentId as Uuid
          )
          const studentRef = fb.getRefById<StudentConstructor>(
            DOC_TYPES.STUDENTS,
            studentId as Uuid
          )
          const snap = await transaction.get(portfolioRef)
          const studentSnap = removedArtworkIds.length
            ? await transaction.get(studentRef)
            : null

          const artworkPatch: Record<string, boolean> = {}
          const remoteArtwork = studentSnap?.data()?.artwork ?? {}
          removedArtworkIds.forEach((artworkId) => {
            const artwork = remoteArtwork[artworkId]
            if (!artwork || artwork.inPortfolio === false) return
            artworkPatch[`artwork.${artworkId}.inPortfolio`] = false
          })

          if (!snap.exists()) {
            transaction.set(
              portfolioRef,
              cleanFormData(nextDoc) as StudentPortfolioConstructor
            )
          } else {
            const remoteNow = snap.data()
            const baselineProjects = existing?.projects ?? {}
            const remoteProjects = remoteNow.projects ?? {}
            const mergedProjects: StudentPortfolioConstructor["projects"] = {
              ...nextDoc.projects,
            }
            let mergedOrder = nextDoc.projectOrder ?? []

            if (!existing) {
              const localIds = new Set(mergedOrder)
              const remoteOnlyIds = (remoteNow.projectOrder ?? []).filter(
                (projectId) =>
                  !localIds.has(projectId) && remoteProjects[projectId]
              )
              remoteOnlyIds.forEach((projectId) => {
                mergedProjects[projectId] = remoteProjects[projectId]
              })
              mergedOrder = [...mergedOrder, ...remoteOnlyIds]
            }

            const baselineUnassigned = baselineProjects[unassignedId]
            const remoteUnassigned = remoteProjects[unassignedId]
            const localUnassigned = nextDoc.projects?.[unassignedId]
            if (remoteUnassigned) {
              const baselineStageIds = new Set(
                Object.keys(baselineUnassigned?.stages ?? {})
              )
              const localStages = { ...(localUnassigned?.stages ?? {}) }
              // Preserve any stage present in remote but absent from both local
              // AND our baseline — that's a scanner addition we never saw.
              // Stages we had in baseline but dropped locally are an intentional
              // delete by the teacher and should stay dropped.
              Object.entries(remoteUnassigned.stages ?? {}).forEach(
                ([stageId, remoteStage]) => {
                  if (localStages[stageId]) return
                  if (baselineStageIds.has(stageId)) return
                  localStages[stageId] = remoteStage
                }
              )
              mergedProjects[unassignedId] = {
                ...(localUnassigned ??
                  remoteUnassigned ??
                  baselineUnassigned ?? { id: unassignedId }),
                ...localUnassigned,
                stages: localStages,
              } as StudentPortfolioConstructor["projects"][string]
            }

            // If the scanner added a brand-new Unassigned project order entry
            // while we had none, preserve it.
            if (
              remoteNow.projectOrder?.includes(unassignedId) &&
              !mergedOrder.includes(unassignedId)
            ) {
              mergedOrder = [...mergedOrder, unassignedId] as Uuid[]
            }

            const cleaned = cleanFormData({
              studentId,
              profile: nextDoc.profile,
              projects: mergedProjects,
              projectOrder: mergedOrder,
              priorityLaneProjectIds: nextDoc.priorityLaneProjectIds,
              timelineEntries: nextDoc.timelineEntries,
            }) as Record<string, unknown>
            transaction.update(portfolioRef, {
              ...cleaned,
              lastUpdatedAt: now,
              lastUpdatedBy: currentUserId,
            })
          }

          if (!isEmpty(artworkPatch)) {
            transaction.update(studentRef, {
              ...artworkPatch,
              lastUpdatedAt: now,
              lastUpdatedBy: currentUserId,
            })
          }
        })
        return true
      } catch (error) {
        void message.error(
          `Failed to save portfolio: ${formatError(error as ErrorType)}`
        )
        return false
      }
    },
    [currentUserId, studentId]
  )

  const newTimelineEvent = useCallback(
    (
      partial: Omit<PortfolioTimelineItem, "authorKind">
    ): PortfolioTimelineItem => ({
      ...partial,
      authorKind,
      createdBy: (currentUserId ?? partial.createdBy) as string,
    }),
    [authorKind, currentUserId]
  )

  // Tells the server to push a mobile notification for a teacher-authored
  // change. Student self-edits never notify. One call per persisted save
  // action, not per changed field — the mobile side collapses to one unread per
  // project anyway.
  const notifyTeacherUpdate = useCallback(
    (projectId: string | undefined) => {
      if (authorKind !== PORTFOLIO_AUTHOR_KIND.TEACHER) return
      if (!studentId || !projectId || !currentUserId) return
      // Essay selection ids aren't real projects — the server endpoint 404s
      // for ids it can't find in the portfolio's `projects` map.
      if (getEssayKeyFromSelectionId(projectId)) return
      void notifyPortfolioUpdated({
        studentId,
        projectId,
        actorUserId: currentUserId,
      })
    },
    [authorKind, currentUserId, studentId]
  )

  // Runs the notification only after the write actually landed, and off the
  // render path.
  const persistThenNotify = useCallback(
    (persist: Promise<boolean>, projectId: string | undefined): Promise<void> =>
      persist.then((saved) => {
        if (saved) notifyTeacherUpdate(projectId)
      }),
    [notifyTeacherUpdate]
  )

  /**
   * Drop on a project tile in the main grid: `sourceId` lands before/after
   * `targetId` and takes the target's category (limit-checked). The drop zone
   * decides which reorder runs — this one for the grid,
   * `reorderProjectPriority` for the lane.
   */
  const reorderProjects = useCallback(
    (
      sourceId: string,
      targetId: string,
      placement: ProjectPlacement = "before"
    ): PortfolioMutationResult => {
      if (sourceId === targetId) return MUTATION_OK
      const result = reorderProjectInArrangement(
        projects,
        sourceId,
        targetId,
        placement
      )
      if (!result.ok) return result
      const entry = newTimelineEvent({
        id: createId("timeline"),
        type: "event",
        projectId: sourceId,
        message: "Updated project order/category.",
        createdAt: new Date().toISOString(),
        createdBy: currentUserId as string,
      })
      const nextTimeline = appendTimelineEntries(timelineEntries, [entry])
      void persistPortfolio(
        result.projects,
        portfolioProfile,
        nextTimeline,
        priorityLaneProjectIds
      )
      return MUTATION_OK
    },
    [
      currentUserId,
      newTimelineEvent,
      persistPortfolio,
      portfolioProfile,
      priorityLaneProjectIds,
      projects,
      timelineEntries,
    ]
  )

  /**
   * Drop on a lane tile, or the hover ⚡ promote/demote: `sourceId` lands
   * before/after `targetId` within `priorityLaneProjectIds`. Both must already
   * be in the lane — membership is add/remove below.
   */
  const reorderProjectPriority = useCallback(
    (
      sourceId: string,
      targetId: string,
      placement: ProjectPlacement = "before"
    ) => {
      if (
        sourceId === targetId ||
        !priorityLaneProjectIds.includes(sourceId) ||
        !priorityLaneProjectIds.includes(targetId)
      ) {
        return
      }
      const nextLane = clampPriorityLane(
        reorderPriorityLane(
          priorityLaneProjectIds,
          sourceId,
          targetId,
          placement
        )
      )
      const entry = newTimelineEvent({
        id: createId("timeline"),
        type: "event",
        projectId: sourceId,
        message: "Updated project priority order.",
        createdAt: new Date().toISOString(),
        createdBy: currentUserId as string,
      })
      void persistPortfolio(
        projects,
        portfolioProfile,
        appendTimelineEntries(timelineEntries, [entry]),
        nextLane
      )
    },
    [
      currentUserId,
      newTimelineEvent,
      persistPortfolio,
      portfolioProfile,
      priorityLaneProjectIds,
      projects,
      timelineEntries,
    ]
  )

  const addProjectToPriorityLane = useCallback(
    (projectId: string) => {
      if (priorityLaneProjectIds.includes(projectId)) return
      if (priorityLaneProjectIds.length >= PORTFOLIO_PRIORITY_LANE_MAX) return
      const nextLane = addToPriorityLane(priorityLaneProjectIds, projectId)
      const entry = newTimelineEvent({
        id: createId("timeline"),
        type: "event",
        projectId,
        message: "Set this project to priority.",
        createdAt: new Date().toISOString(),
        createdBy: currentUserId as string,
      })
      void persistPortfolio(
        projects,
        portfolioProfile,
        appendTimelineEntries(timelineEntries, [entry]),
        nextLane
      )
    },
    [
      currentUserId,
      newTimelineEvent,
      persistPortfolio,
      portfolioProfile,
      priorityLaneProjectIds,
      projects,
      timelineEntries,
    ]
  )

  const removeProjectFromPriorityLane = useCallback(
    (projectId: string) => {
      if (!priorityLaneProjectIds.includes(projectId)) return
      const nextLane = removeFromPriorityLane(priorityLaneProjectIds, projectId)
      const entry = newTimelineEvent({
        id: createId("timeline"),
        type: "event",
        projectId,
        message: "Removed this project from priority.",
        createdAt: new Date().toISOString(),
        createdBy: currentUserId as string,
      })
      void persistPortfolio(
        projects,
        portfolioProfile,
        appendTimelineEntries(timelineEntries, [entry]),
        nextLane
      )
    },
    [
      currentUserId,
      newTimelineEvent,
      persistPortfolio,
      portfolioProfile,
      priorityLaneProjectIds,
      projects,
      timelineEntries,
    ]
  )

  const moveStageToProject = useCallback(
    (stageId: string, sourceProjectId: string, targetProjectId: string) => {
      if (sourceProjectId === targetProjectId) return
      const sourceProject = projects.find((p) => p.id === sourceProjectId)
      const targetProject = projects.find((p) => p.id === targetProjectId)
      if (!sourceProject || !targetProject) return
      const stageToMove = sourceProject.stages.find((s) => s.id === stageId)
      if (!stageToMove) return
      const next = projects.map((project) => {
        if (project.id === sourceProjectId) {
          return withStageSortIndices({
            ...project,
            stages: project.stages.filter((s) => s.id !== stageId),
          })
        }
        if (project.id === targetProjectId) {
          return withStageSortIndices({
            ...project,
            stages: [stageToMove, ...project.stages],
          })
        }
        return project
      })
      const entry = newTimelineEvent({
        id: createId("timeline"),
        type: "event",
        projectId: targetProjectId,
        message: "Moved project step to a different project.",
        createdAt: new Date().toISOString(),
        createdBy: currentUserId as string,
      })
      void persistPortfolio(
        next,
        portfolioProfile,
        appendTimelineEntries(timelineEntries, [entry]),
        priorityLaneProjectIds
      )
    },
    [
      currentUserId,
      newTimelineEvent,
      persistPortfolio,
      portfolioProfile,
      priorityLaneProjectIds,
      projects,
      timelineEntries,
    ]
  )

  const reorderStagesInProject = useCallback(
    (projectId: string, sourceStageId: string, targetStageId: string) => {
      const next = projects.map((project) => {
        if (project.id !== projectId) return project
        const sourceIndex = project.stages.findIndex(
          (s) => s.id === sourceStageId
        )
        const targetIndex = project.stages.findIndex(
          (s) => s.id === targetStageId
        )
        if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) {
          return project
        }
        const nextStages = [...project.stages]
        const [moved] = nextStages.splice(sourceIndex, 1)
        nextStages.splice(targetIndex, 0, moved)
        return withStageSortIndices({ ...project, stages: nextStages })
      })
      const entry = newTimelineEvent({
        id: createId("timeline"),
        type: "event",
        projectId,
        message: "Reordered project steps.",
        createdAt: new Date().toISOString(),
        createdBy: currentUserId as string,
      })
      void persistPortfolio(
        next,
        portfolioProfile,
        appendTimelineEntries(timelineEntries, [entry]),
        priorityLaneProjectIds
      )
    },
    [
      currentUserId,
      newTimelineEvent,
      persistPortfolio,
      portfolioProfile,
      priorityLaneProjectIds,
      projects,
      timelineEntries,
    ]
  )

  const deleteStage = useCallback(
    (projectId: string, stageId: string) => {
      const project = projects.find((p) => p.id === projectId)
      const stage = project?.stages.find((s) => s.id === stageId)
      if (!project || !stage) return
      const next = projects.map((p) => {
        if (p.id !== projectId) return p
        return withStageSortIndices({
          ...p,
          stages: p.stages.filter((s) => s.id !== stageId),
        })
      })
      const entry = newTimelineEvent({
        id: createId("timeline"),
        type: "event",
        projectId,
        message: `Deleted project step: ${stage.label}.`,
        createdAt: new Date().toISOString(),
        createdBy: currentUserId as string,
      })
      void persistPortfolio(
        next,
        portfolioProfile,
        appendTimelineEntries(timelineEntries, [entry]),
        priorityLaneProjectIds,
        stage.artworkId ? [stage.artworkId] : []
      )
    },
    [
      currentUserId,
      newTimelineEvent,
      persistPortfolio,
      portfolioProfile,
      priorityLaneProjectIds,
      projects,
      timelineEntries,
    ]
  )

  const deleteProject = useCallback(
    (projectId: string) => {
      const deletedIndex = projects.findIndex((p) => p.id === projectId)
      if (deletedIndex < 0) return
      const project = projects[deletedIndex]
      if (project.category === "unassigned") return
      const nextProjects = normalizeArrangement(
        projects.filter((p) => p.id !== projectId)
      )
      const nextLane = removeFromPriorityLane(priorityLaneProjectIds, projectId)
      const prunedTimeline = timelineEntries.filter(
        (entry) => entry.projectId !== projectId
      )
      const entry = newTimelineEvent({
        id: createId("timeline"),
        type: "event",
        message: `Deleted project: ${project.title}.`,
        createdAt: new Date().toISOString(),
        createdBy: currentUserId as string,
      })
      if (activeProjectId === projectId) {
        // Prefer next in list, then previous, then fall back to Unassigned.
        const nextNeighbour = nextProjects[deletedIndex]
        const prevNeighbour = nextProjects[deletedIndex - 1]
        const unassigned = nextProjects.find((p) => p.category === "unassigned")
        const fallbackProject = nextNeighbour ?? prevNeighbour ?? unassigned
        setActiveProjectId(fallbackProject?.id)
      }
      void persistPortfolio(
        nextProjects,
        portfolioProfile,
        appendTimelineEntries(prunedTimeline, [entry]),
        nextLane,
        project.stages
          .map((stage) => stage.artworkId)
          .filter((artworkId): artworkId is string => !!artworkId)
      )
    },
    [
      activeProjectId,
      currentUserId,
      newTimelineEvent,
      persistPortfolio,
      portfolioProfile,
      priorityLaneProjectIds,
      projects,
      timelineEntries,
    ]
  )

  /**
   * Field edits on a project. A `category` change is a move between groups and
   * goes through the arrangement rules (limits, fixed Unassigned) — the only
   * path that changes a category, so the limits hold wherever the edit came
   * from. `tagIds` are de-duplicated before the write.
   */
  const updateProject = useCallback(
    (
      projectId: string,
      rawUpdates: Partial<
        Pick<
          PortfolioMockProject,
          | "title"
          | "notes"
          | "coverImageUrl"
          | "category"
          | "planningGroup"
          | "tagIds"
          | "lastActivityAt"
          | "dimensions"
          | "materials"
          | "processes"
          | "ideas"
        >
      >
    ): PortfolioMutationResult => {
      const project = projects.find((p) => p.id === projectId)
      if (!project) return { ok: false, error: { code: "not_found" } }
      const updates: typeof rawUpdates = {
        ...rawUpdates,
        ...(rawUpdates.tagIds !== undefined
          ? { tagIds: normalizeArtworkTagIds(rawUpdates.tagIds) }
          : {}),
      }

      let arranged: PortfolioMockProject[] = projects
      if (
        updates.category !== undefined &&
        updates.category !== project.category
      ) {
        const moved = moveProjectToCategoryInArrangement(
          projects,
          projectId,
          updates.category
        )
        if (!moved.ok) return moved
        arranged = moved.projects
      }

      const updatedAt = new Date().toISOString()
      const changedEntries: PortfolioTimelineItem[] = (
        Object.keys(updates) as Array<keyof typeof updates>
      )
        .filter((fieldKey) => {
          const nextValue = updates[fieldKey]
          if (nextValue === undefined) return false
          const previous = project[fieldKey]
          return Array.isArray(nextValue) && Array.isArray(previous)
            ? nextValue.join("\u0000") !== previous.join("\u0000")
            : previous !== nextValue
        })
        .map((fieldKey) => {
          // The timeline is read by people, so tag ids are resolved to their
          // dictionary text here; an id the dictionary lost is dropped.
          const eventValue =
            fieldKey === "tagIds"
              ? resolveArtworkTagTexts(updates.tagIds ?? [], tagsById)
              : updates[fieldKey]
          const nextValue = formatEventValue(fieldKey, eventValue)
          return newTimelineEvent({
            id: createId("timeline"),
            type: "event",
            projectId,
            message: buildFieldEventMessage({
              scope: "project",
              fieldKey,
              value: eventValue,
            }),
            createdAt: updatedAt,
            createdBy: currentUserId as string,
            eventScope: "project",
            eventEntityId: projectId,
            eventFieldKey: fieldKey,
            eventNextValue: nextValue,
          })
        })
      // `arranged` already carries the new category + index; spread the rest
      // of the updates over it (the category value is identical either way).
      const next = arranged.map((p) =>
        p.id === projectId
          ? {
              ...p,
              ...updates,
              category: p.category,
              lastActivityAt: updatedAt,
            }
          : p
      )
      const nextTimeline =
        changedEntries.length > 0
          ? appendTimelineEntries(timelineEntries, changedEntries)
          : timelineEntries
      void persistThenNotify(
        persistPortfolio(
          next,
          portfolioProfile,
          nextTimeline,
          priorityLaneProjectIds
        ),
        projectId
      )
      return MUTATION_OK
    },
    [
      currentUserId,
      newTimelineEvent,
      persistPortfolio,
      persistThenNotify,
      portfolioProfile,
      priorityLaneProjectIds,
      projects,
      tagsById,
      timelineEntries,
    ]
  )

  const updateStage = useCallback(
    (
      projectId: string,
      stageId: string,
      updates: Partial<
        Pick<
          PortfolioMockStage,
          | "label"
          | "dueDate"
          | "medium"
          | "notes"
          | "teacherComment"
          | "feedback"
          | "status"
          | "imageUrl"
          | "artworkId"
          | "rejectionReason"
          | "lastActivityAt"
          | "planningGroup"
          | "sortIndex"
          | "customFields"
        >
      >
    ) => {
      const normalizedUpdates: typeof updates =
        (updates.feedback !== undefined ||
          updates.teacherComment !== undefined) &&
        updates.status === undefined
          ? {
              ...updates,
              ...((updates.feedback ?? updates.teacherComment ?? "").trim() !==
              ""
                ? { status: "has_feedback" as PortfolioMockStage["status"] }
                : {}),
            }
          : updates

      const project = projects.find((p) => p.id === projectId)
      const stage = project?.stages.find((s) => s.id === stageId)
      const updatedAt = new Date().toISOString()
      const changedEntries: PortfolioTimelineItem[] = stage
        ? (
            Object.keys(normalizedUpdates) as Array<
              keyof typeof normalizedUpdates
            >
          )
            .filter((fieldKey) => {
              const nextValue = normalizedUpdates[fieldKey]
              if (FEEDBACK_TIMELINE_ITEM_KEYS.has(fieldKey as string)) {
                return false
              }
              return (
                nextValue !== undefined &&
                stage[fieldKey as keyof PortfolioMockStage] !== nextValue
              )
            })
            .map((fieldKey) => {
              const rawNext = normalizedUpdates[fieldKey]
              const nextValue = formatEventValue(fieldKey, rawNext)
              const stepLabel = stage.label?.trim() || "step"
              const stepNumber =
                (project?.stages.findIndex((s) => s.id === stageId) ?? -1) + 1
              let message: string
              if (fieldKey === "imageUrl") {
                const url = typeof rawNext === "string" ? rawNext.trim() : ""
                message = url
                  ? `Uploaded an image to ${stepLabel}.`
                  : `Removed image from ${stepLabel}.`
              } else if (fieldKey === "feedback") {
                const preview =
                  typeof rawNext === "string" ? rawNext.trim() : ""
                message = preview
                  ? `Left feedback on Step ${stepNumber} "${stepLabel}": ${preview}`
                  : `Cleared feedback on Step ${stepNumber} "${stepLabel}".`
              } else {
                message = buildFieldEventMessage({
                  scope: "item",
                  fieldKey,
                  value: rawNext,
                })
              }
              return newTimelineEvent({
                id: createId("timeline"),
                type: "event",
                projectId,
                message,
                createdAt: updatedAt,
                createdBy: currentUserId as string,
                eventScope: "item",
                eventEntityId: stageId,
                eventFieldKey: fieldKey,
                eventNextValue: nextValue,
              })
            })
        : []

      const next = projects.map((p) => {
        if (p.id !== projectId) return p
        return {
          ...p,
          lastActivityAt: updatedAt,
          stages: p.stages.map((s) =>
            s.id === stageId
              ? { ...s, ...normalizedUpdates, lastActivityAt: updatedAt }
              : s
          ),
        }
      })
      const nextTimeline =
        changedEntries.length > 0
          ? appendTimelineEntries(timelineEntries, changedEntries)
          : timelineEntries
      void persistThenNotify(
        persistPortfolio(
          next,
          portfolioProfile,
          nextTimeline,
          priorityLaneProjectIds
        ),
        projectId
      )
    },
    [
      currentUserId,
      newTimelineEvent,
      persistPortfolio,
      persistThenNotify,
      portfolioProfile,
      priorityLaneProjectIds,
      projects,
      timelineEntries,
    ]
  )

  const addArtworkToProject = useCallback(
    ({
      projectId,
      imageUrl,
      label,
    }: {
      projectId: string
      imageUrl: string
      label: string
    }) => {
      const now = new Date().toISOString()
      const newStage: PortfolioMockStage = {
        id: createId("stage"),
        label,
        status: "in_progress",
        dueDate: now,
        medium: "",
        notes: "",
        teacherComment: "",
        feedback: "",
        imageUrl,
        createdAt: now,
        createdBy: currentUserId as string,
        customFields: {},
      }
      const next = projects.map((project) => {
        if (project.id !== projectId) return project
        return withStageSortIndices({
          ...project,
          stages: [newStage, ...project.stages],
        })
      })
      const entry = newTimelineEvent({
        id: createId("timeline"),
        type: "event",
        projectId,
        message: `Added a new project step: ${label}.`,
        createdAt: now,
        createdBy: currentUserId as string,
      })
      void persistPortfolio(
        next,
        portfolioProfile,
        appendTimelineEntries(timelineEntries, [entry]),
        priorityLaneProjectIds
      )
    },
    [
      currentUserId,
      newTimelineEvent,
      persistPortfolio,
      portfolioProfile,
      priorityLaneProjectIds,
      projects,
      timelineEntries,
    ]
  )

  const createStep = useCallback(
    (input: CreatePortfolioStepInput) => {
      const {
        projectId,
        label,
        dueDate,
        status = "not_started",
        artworkId = "",
        medium = "",
        notes = "",
        teacherComment = "",
        feedback = "",
        imageUrl = "",
      } = input
      const now = new Date().toISOString()
      const trimmedLabel = label.trim()
      if (!trimmedLabel) return
      const targetProject = projects.find((project) => project.id === projectId)
      if (
        targetProject &&
        targetProject.stages.length >= PORTFOLIO_MAX_STEPS_PER_PROJECT
      ) {
        return
      }
      const newStage: PortfolioMockStage = {
        id: createId("stage"),
        label: trimmedLabel,
        status,
        dueDate: dueDate ?? "",
        artworkId: artworkId || undefined,
        medium,
        notes,
        teacherComment,
        feedback,
        imageUrl,
        createdAt: now,
        createdBy: currentUserId as string,
        lastActivityAt: now,
        customFields: {},
      }
      const next = projects.map((project) => {
        if (project.id !== projectId) return project
        return withStageSortIndices({
          ...project,
          stages: [...project.stages, newStage],
        })
      })
      const entry = newTimelineEvent({
        id: createId("timeline"),
        type: "event",
        projectId,
        message: `Created a new project step: ${trimmedLabel}.`,
        createdAt: now,
        createdBy: currentUserId as string,
      })
      void persistPortfolio(
        next,
        portfolioProfile,
        appendTimelineEntries(timelineEntries, [entry]),
        priorityLaneProjectIds
      )
    },
    [
      currentUserId,
      newTimelineEvent,
      persistPortfolio,
      portfolioProfile,
      priorityLaneProjectIds,
      projects,
      timelineEntries,
    ]
  )

  /**
   * New projects go to the front of their category (limit-checked). Returns
   * `ok: false` without writing when the category is full, so callers can
   * tell the teacher instead of silently dropping the project.
   */
  const createProject = useCallback(
    (input: CreatePortfolioProjectInput): PortfolioMutationResult => {
      const {
        title,
        category = "sustained",
        planningGroup,
        tagIds = [],
        coverImageUrl = "",
        notes = "",
        dimensions = "",
        materials = "",
        processes = "",
        ideas = "",
        initialStepImageUrl,
        initialStepLabel,
        initialStepArtworkId,
      } = input
      const trimmedTitle = title.trim()
      if (!trimmedTitle) return { ok: false, error: { code: "empty_title" } }
      const now = new Date().toISOString()
      const projectId = createId("project")
      const initialStages: PortfolioMockStage[] = initialStepImageUrl
        ? [
            {
              id: createId("stage"),
              label: initialStepLabel || trimmedTitle,
              status: "in_progress",
              dueDate: "",
              artworkId: initialStepArtworkId,
              medium: "",
              notes: "",
              teacherComment: "",
              feedback: "",
              imageUrl: initialStepImageUrl,
              createdAt: now,
              createdBy: currentUserId as string,
              customFields: {},
            },
          ]
        : []
      const project: PortfolioMockProject = {
        id: projectId,
        title: trimmedTitle,
        category,
        planningGroup,
        coverImageUrl,
        notes,
        dimensions,
        materials,
        processes,
        ideas,
        tagIds: normalizeArtworkTagIds(tagIds),
        stages: initialStages,
        createdAt: now,
        createdBy: currentUserId as string,
        lastActivityAt: now,
      }
      const inserted = insertNewProjectInArrangement(projects, project)
      if (!inserted.ok) return inserted
      const entry = newTimelineEvent({
        id: createId("timeline"),
        type: "event",
        projectId,
        message: `Created a new project “${trimmedTitle}”.`,
        createdAt: now,
        createdBy: currentUserId as string,
      })
      setActiveProjectId(projectId)
      void persistPortfolio(
        inserted.projects,
        portfolioProfile,
        appendTimelineEntries(timelineEntries, [entry]),
        priorityLaneProjectIds
      )
      return MUTATION_OK
    },
    [
      currentUserId,
      newTimelineEvent,
      persistPortfolio,
      portfolioProfile,
      priorityLaneProjectIds,
      projects,
      timelineEntries,
    ]
  )

  const addComment = useCallback(
    ({
      message,
      projectId,
      createdBy,
    }: {
      message: string
      projectId?: string
      createdBy?: string
    }) => {
      const trimmed = message.trim()
      if (!trimmed) return
      const by = (createdBy ?? currentUserId) as string
      const entry: PortfolioTimelineItem = {
        id: createId("timeline"),
        type: "comment",
        projectId,
        message: trimmed,
        createdAt: new Date().toISOString(),
        createdBy: by,
        authorKind,
      }
      void persistThenNotify(
        persistPortfolio(
          projects,
          portfolioProfile,
          [entry, ...timelineEntries],
          priorityLaneProjectIds
        ),
        projectId
      )
    },
    [
      authorKind,
      currentUserId,
      persistPortfolio,
      persistThenNotify,
      portfolioProfile,
      priorityLaneProjectIds,
      projects,
      timelineEntries,
    ]
  )

  const updatePortfolioProfile = useCallback(
    (updates: Partial<PortfolioMockProfile>) => {
      const base = derived.portfolioProfile
      const merged: PortfolioMockProfile = {
        ...base,
        ...updates,
        statements: {
          ...base.statements,
          ...(updates.statements ?? {}),
          sustainedInquiryQuestion: {
            ...base.statements.sustainedInquiryQuestion,
            ...updates.statements?.sustainedInquiryQuestion,
          },
          sustainedPracticeEvidence: {
            ...base.statements.sustainedPracticeEvidence,
            ...updates.statements?.sustainedPracticeEvidence,
          },
        },
      }
      void persistPortfolio(
        projects,
        mergeProfileWithEphemeralFeedback(merged, ephemeralTeacherFeedback),
        timelineEntries,
        priorityLaneProjectIds
      )
    },
    [
      derived.portfolioProfile,
      ephemeralTeacherFeedback,
      persistPortfolio,
      priorityLaneProjectIds,
      projects,
      timelineEntries,
    ]
  )

  const updateStatementValue = useCallback(
    (statementKey: PortfolioStatementKey, value: string) => {
      const base = derived.portfolioProfile
      const current = base.statements[statementKey]
      const nextStatus =
        current.status === "approved" ? "submitted" : current.status
      const merged: PortfolioMockProfile = {
        ...base,
        statements: {
          ...base.statements,
          [statementKey]: {
            ...current,
            value,
            status: nextStatus,
          },
        },
      }
      const essaySelectionId = ESSAY_SELECTION_IDS[statementKey]
      const entry = newTimelineEvent({
        id: createId("timeline"),
        type: "event",
        projectId: essaySelectionId,
        message: `Updated ${ESSAY_LABELS[statementKey]}.`,
        createdAt: new Date().toISOString(),
        createdBy: currentUserId as string,
        eventScope: "project",
        eventEntityId: essaySelectionId,
        eventFieldKey: statementKey,
        eventNextValue: value,
      })
      const nextTimeline =
        value !== current.value
          ? appendTimelineEntries(timelineEntries, [entry])
          : timelineEntries
      void persistPortfolio(
        projects,
        mergeProfileWithEphemeralFeedback(merged, ephemeralTeacherFeedback),
        nextTimeline,
        priorityLaneProjectIds
      )
    },
    [
      currentUserId,
      derived.portfolioProfile,
      ephemeralTeacherFeedback,
      newTimelineEvent,
      persistPortfolio,
      priorityLaneProjectIds,
      projects,
      timelineEntries,
    ]
  )

  const submitStatement = useCallback(
    (statementKey: PortfolioStatementKey, submittedBy: string) => {
      const submittedAt = new Date().toISOString()
      const base = derived.portfolioProfile
      const merged: PortfolioMockProfile = {
        ...base,
        statements: {
          ...base.statements,
          [statementKey]: {
            ...base.statements[statementKey],
            status: "submitted",
            submittedAt,
            submittedBy,
          },
        },
      }
      void persistPortfolio(
        projects,
        mergeProfileWithEphemeralFeedback(merged, ephemeralTeacherFeedback),
        timelineEntries,
        priorityLaneProjectIds
      )
    },
    [
      derived.portfolioProfile,
      ephemeralTeacherFeedback,
      persistPortfolio,
      priorityLaneProjectIds,
      projects,
      timelineEntries,
    ]
  )

  const approveStatement = useCallback(
    ({
      statementKey,
      reviewedBy,
      teacherFeedback,
    }: {
      statementKey: PortfolioStatementKey
      reviewedBy: string
      teacherFeedback?: string
    }) => {
      const reviewedAt = new Date().toISOString()
      const base = derived.portfolioProfile
      const merged: PortfolioMockProfile = {
        ...base,
        statements: {
          ...base.statements,
          [statementKey]: {
            ...base.statements[statementKey],
            status: "approved",
            reviewedAt,
            reviewedBy,
            teacherFeedback:
              teacherFeedback ?? base.statements[statementKey].teacherFeedback,
          },
        },
      }
      void persistPortfolio(
        projects,
        mergeProfileWithEphemeralFeedback(merged, ephemeralTeacherFeedback),
        timelineEntries,
        priorityLaneProjectIds
      )
    },
    [
      derived.portfolioProfile,
      ephemeralTeacherFeedback,
      persistPortfolio,
      priorityLaneProjectIds,
      projects,
      timelineEntries,
    ]
  )

  const rejectStatement = useCallback(
    ({
      statementKey,
      reviewedBy,
      rejectionComment,
    }: {
      statementKey: PortfolioStatementKey
      reviewedBy: string
      rejectionComment: string
    }): boolean => {
      const trimmedComment = rejectionComment.trim()
      if (!trimmedComment) return false
      const reviewedAt = new Date().toISOString()
      const base = derived.portfolioProfile
      const merged: PortfolioMockProfile = {
        ...base,
        statements: {
          ...base.statements,
          [statementKey]: {
            ...base.statements[statementKey],
            status: "rejected",
            reviewedAt,
            reviewedBy,
            teacherFeedback: trimmedComment,
          },
        },
      }
      void persistPortfolio(
        projects,
        mergeProfileWithEphemeralFeedback(merged, ephemeralTeacherFeedback),
        timelineEntries,
        priorityLaneProjectIds
      )
      return true
    },
    [
      derived.portfolioProfile,
      ephemeralTeacherFeedback,
      persistPortfolio,
      priorityLaneProjectIds,
      projects,
      timelineEntries,
    ]
  )

  const updateStatementTeacherFeedback = useCallback(
    ({
      statementKey,
      teacherFeedback,
    }: {
      statementKey: PortfolioStatementKey
      reviewedBy?: string
      teacherFeedback: string
    }) => {
      setEphemeralTeacherFeedback((prev) => ({
        ...prev,
        [statementKey]: teacherFeedback,
      }))
    },
    []
  )

  const addProjectReference = useCallback(
    (projectId: string, imageUrl: string) => {
      const referenceId = createId("reference")
      const next = projects.map((project) => {
        if (project.id !== projectId) return project
        const nextReferences = project.references ?? []
        return {
          ...project,
          references: [
            {
              id: referenceId,
              imageUrl,
              title: "",
              notes: "",
              teacherComment: "",
            },
            ...nextReferences,
          ],
        }
      })
      void persistPortfolio(
        next,
        portfolioProfile,
        timelineEntries,
        priorityLaneProjectIds
      )
    },
    [
      persistPortfolio,
      portfolioProfile,
      priorityLaneProjectIds,
      projects,
      timelineEntries,
    ]
  )

  const updateProjectReference = useCallback(
    (
      projectId: string,
      referenceId: string,
      updates: Partial<
        Pick<
          PortfolioMockReference,
          "title" | "notes" | "teacherComment" | "imageUrl"
        >
      >
    ) => {
      const keys = Object.keys(updates)
      const onlyFeedback =
        keys.length > 0 && keys.every((k) => k === "teacherComment")
      const next = projects.map((project) => {
        if (project.id !== projectId) return project
        return {
          ...project,
          references: (project.references ?? []).map((reference) =>
            reference.id === referenceId
              ? { ...reference, ...updates }
              : reference
          ),
        }
      })
      let nextTimeline = timelineEntries
      if (!onlyFeedback) {
        const entry = newTimelineEvent({
          id: createId("timeline"),
          type: "event",
          projectId,
          message: "Updated project reference.",
          createdAt: new Date().toISOString(),
          createdBy: currentUserId as string,
        })
        nextTimeline = appendTimelineEntries(timelineEntries, [entry])
      }
      void persistPortfolio(
        next,
        portfolioProfile,
        nextTimeline,
        priorityLaneProjectIds
      )
    },
    [
      currentUserId,
      newTimelineEvent,
      persistPortfolio,
      portfolioProfile,
      priorityLaneProjectIds,
      projects,
      timelineEntries,
    ]
  )

  const removeProjectReference = useCallback(
    (projectId: string, referenceId: string) => {
      const project = projects.find((p) => p.id === projectId)
      const removedRef = project?.references?.find((r) => r.id === referenceId)
      if (removedRef?.imageUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(removedRef.imageUrl)
      }
      const next = projects.map((project) => {
        if (project.id !== projectId) return project
        return {
          ...project,
          references: (project.references ?? []).filter(
            (reference) => reference.id !== referenceId
          ),
        }
      })
      void persistPortfolio(
        next,
        portfolioProfile,
        timelineEntries,
        priorityLaneProjectIds
      )
    },
    [
      persistPortfolio,
      portfolioProfile,
      priorityLaneProjectIds,
      projects,
      timelineEntries,
    ]
  )

  const planningProjectSummaries = useMemo(
    () =>
      Object.fromEntries(
        projects.map((project) => [
          project.id,
          getProjectPlanningSummary(project),
        ])
      ) as Record<string, PortfolioPlanningProjectSummary>,
    [projects]
  )

  return {
    activeProjectId,
    /** False until the portfolio document has arrived from Firestore. */
    isPortfolioLoaded: portfolioDoc != null,
    moveStageToProject,
    projects,
    planningProjectSummaries,
    priorityLaneProjectIds,
    addProjectToPriorityLane,
    removeProjectFromPriorityLane,
    reorderProjectPriority,
    reorderProjects,
    reorderStagesInProject,
    deleteStage,
    deleteProject,
    updateProject,
    updateStage,
    addArtworkToProject,
    createProject,
    createStep,
    addProjectReference,
    addComment,
    approveStatement,
    timelineEntries,
    portfolioProfile,
    removeProjectReference,
    rejectStatement,
    selectedEssayKey,
    selectedProject,
    selectedWorkProjects,
    setActiveProjectId,
    submitStatement,
    sustainedProjects,
    pausedProjects,
    unassignedProjects,
    updatePortfolioProfile,
    updateProjectReference,
    updateStatementTeacherFeedback,
    updateStatementValue,
    pickRandomProjectIdFromLane,
  }
}
